import {
    DosageTimesInstructionForm
} from "@communicare/medications/MedicationOverview/Fields/DosageInstructions/DosageTimesInstructionsForm";
import React, { useEffect, useRef, useState } from "react";
import { InstructionType } from "@communicare/medications/Contracts/MedicationModels";
import { getMedicationInstructions } from "../../services/medicationmanagement/medications/MedicationsApi";
import { ComponentLoader } from "../common";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles } from "@material-ui/core";
import { logger } from "../common/logger";
import { MedicationInstruction } from "../../services/medicationmanagement/medications/models/MedicationInstruction";
import { Nullable } from "@communicare/medications/Shared/Types";
import { Reference } from "@communicare/medications/Shared/SharedModels";
import { Box, Column, Flex } from "@dna/react";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { CourseType } from "../../services/medicationmanagement/medications";
import { medicationsService, } from "../../services/medicationmanagement/medications";
import { PackagingType } from "../../services/medicationmanagement/medications/models/PackagingType";
import { communicareMedicationService } from "../../services/communicare/MedicationsApi";
import moment from 'moment-timezone';

export const DoseTimesEditorForm = (props: {
    instructions: string;
    doseTimes: string[];
    routeOfAdministration: Nullable<Reference>;
    frequency: Nullable<Reference>;
    startsOn: string;
    onChange: (val: {
        times: string[],
        frequency: Nullable<Reference>,
        startsOn: Nullable<moment.Moment>
    }) => void;
}) => {
    return <DosageTimesInstructionForm instruction={{
        instructions: props.instructions,
        times: props.doseTimes,
        type: InstructionType.DosageTimeInstruction,
        routeOfAdministration: props.routeOfAdministration,
        frequency: props.frequency,
        textInstructions: '',
        startsOn: props.startsOn
    }}
        readOnly={false}
        disableInstructions={true}
        disableRouteOfAdministration={true}
        disableSentenceBuilder={true}
        disableStartsOn={true}
        label='Administration dosage instructions'
        postApi={_ => Promise.resolve({ id: '' })}
        onUpdate={data => {
            props.onChange(data)
        }
        }
    />

}
export interface IDoseTimesEditorProps {
    medicationAuthoringSystem: string;
    medicationAuthoringSystemForeignKey: string;
    medicationName: string;
    medSummary: { courseType?: CourseType, medicationId: string, packagingType?: PackagingType; };
    onConfirmed: () => void;
    onCancel: () => void;
}


type ProgressState = 'Loading' | 'Error' | 'Loaded' | 'Confirmed';

type DoseInstruction = Omit<MedicationInstruction, 'courseInstructions'>;
class DoseTimesAndPackingManager {
    private doseTimes: string[] = [];
    private instructions: string | null = null;
    private routeOfAdministration?: { code: string; name: string };
    private progressState: 'Loading' | 'Error' | 'Loaded' | 'Confirmed' = 'Loading';
    private subscribers: ((state: [ProgressState, DoseInstruction | null]) => void)[] = [];
    private frequency: { code: string; name: string } | undefined;
    private packingType?: PackagingType = undefined;
    private startsOn: string = '';

    constructor(private medicationAuthoringSystem: string, private medicationAuthoringSystemForeignKey: string) {

    }

    loadDoseTimes() {
        getMedicationInstructions(this.medicationAuthoringSystem, this.medicationAuthoringSystemForeignKey)
            .then(instruction => {
                this.doseTimes = instruction.doseTimes || [];
                this.instructions = instruction.textInstruction;
                this.routeOfAdministration = instruction.routeOfAdministration;
                this.frequency = instruction.frequency;
                this.startsOn = instruction.startsOn;
                this.progressState = 'Loaded'
                this.notify();
            })
            .catch(_ => {
                this.progressState = 'Error';
                this.doseTimes = [];
                this.instructions = null;
                this.notify();
            });
    }
    private notify() {
        this.subscribers.forEach(s => s([this.progressState, {
            textInstruction: this.instructions || '',
            doseTimes: this.doseTimes,
            routeOfAdministration: this.routeOfAdministration,
            frequency: this.frequency,
            startsOn: this.startsOn
        }]));
    }
    subscribe(subscriber: (data: [ProgressState, DoseInstruction | null]) => void) {
        this.subscribers.push(subscriber);
    }

    changeDoseTimes(doseTimes: { times: string[], frequency: Nullable<Reference>, startsOn: Nullable<moment.Moment> }) {
        this.doseTimes = [...doseTimes.times];
        this.frequency = doseTimes.frequency || undefined;
        this.startsOn = doseTimes.startsOn?.format() || '';
        this.notify();
    }

    setMedicationPacking(type?: PackagingType) {
        this.packingType = type;
    }

    async confirmChanges() {
        const packingStautsChange = await this.confirmPackingStatusChange();
        const doseTimeChanges = this.confirmDoseTimeChanges();

        Promise.all([
            packingStautsChange,
            doseTimeChanges
        ]).then(_ => {
            this.progressState = 'Confirmed';
            this.notify();
        })
            .catch(err => {
                this.progressState = 'Error';
                this.notify();
            });
    }

    async confirmPackingStatusChange() {
        await medicationsService.changeMedicationPackaging(
            this.medicationAuthoringSystem,
            this.medicationAuthoringSystemForeignKey,
            this.packingType?.toString() || ''
        );
    }

    confirmDoseTimeChanges() {
        communicareMedicationService.editDosageTimes({
            times: this.doseTimes,
            instructions: this.instructions || '',
            routeOfAdministration: this.routeOfAdministration && this.routeOfAdministration?.code ? {
                code: this.routeOfAdministration.code,
                name: this.routeOfAdministration.name
            } : null,
            frequency: this.frequency && this.frequency.code ? {
                code: this.frequency.code,
                name: this.frequency.name
            } : null,
            medicationAuthoringSystem: this.medicationAuthoringSystem,
            medicationAuthoringSystemForeignKey: this.medicationAuthoringSystemForeignKey,
            startsOn: this.startsOn
        })

    }

}

export const PackingStatusEditor = (prop: { selectedPacking?: PackagingType, onChange: (packingType: PackagingType) => void; }) => {

    const useStyles = makeStyles((theme) => ({
        button: {
            textTransform: "none",
            color: "#006AD2",
            fontSize: 14,
            fontWeight: "bolder",
            border: "1px solid #D2D2D2",
            "&:hover": {
                backgroundColor: 'rgba(33, 137, 214, 0.14)',
                color: 'rgb(26, 88, 159)',
            },
            "&:focus": {
                backgroundColor: 'transparent',
            },
        },

        selected: {
            "&&": {
                backgroundColor: "#006AD2",
                color: 'rgb(255, 255, 255)',
                border: "1px solid #D2D2D2",
                '&:hover': {
                    backgroundColor: 'rgb(51, 136, 219);',
                    color: 'rgb(255, 255, 255)',
                }
            },
        }
    }));
    const [medPacking, setMedicationPacking] = React.useState<PackagingType | undefined>(prop.selectedPacking);

    const handlePackingChange = (event: any, packingType: PackagingType) => {
        setMedicationPacking(packingType);
        prop.onChange(packingType);
    };

    let classes = useStyles();
    return <Box css={{
        paddingBottom: "15px"
    }}>
        <Column css={{
            paddingBottom: "5px"
        }}>
            Category
        </Column>
        <ToggleButtonGroup
            size='small'
            color="primary"
            value={medPacking}
            exclusive
            onChange={handlePackingChange}>
            <ToggleButton classes={{ root: classes.button, selected: classes.selected }} value="Packed"  >Packed</ToggleButton>
            <ToggleButton classes={{ root: classes.button, selected: classes.selected }} value="Unpacked">Unpacked</ToggleButton>
        </ToggleButtonGroup>
    </Box>
}

export const AdjustMedicationInfoEditor = (props: IDoseTimesEditorProps) => {
    let {
        medicationAuthoringSystem
        , medicationAuthoringSystemForeignKey
        , medicationName
        , medSummary
        , onConfirmed
        , onCancel
    } = props;
    let [dialogState, changeDialogState] = useState<'Open' | 'Close'>('Open');

    let [doseTimes, changeDoseTimes] = useState<[ProgressState, DoseInstruction | null]>(['Loading', null]);
    let doseTimeManager = useRef<DoseTimesAndPackingManager | null>(new DoseTimesAndPackingManager(medicationAuthoringSystem, medicationAuthoringSystemForeignKey));

    useEffect(() => {
        var doseManager = doseTimeManager.current;
        doseManager?.subscribe(changeDoseTimes);
        doseManager?.loadDoseTimes();
        doseManager?.setMedicationPacking(medSummary.packagingType)
        return () => {
            changeDialogState('Close');
            doseTimeManager.current = null;
        };
    }, []);
    useEffect(() => {
        if (doseTimes[0] === 'Confirmed') {
            onConfirmed();
        }
    }, [doseTimes[0]]);


    let progressState = doseTimes[0];
    let orderInstruction = doseTimes[1];
    return <Dialog open={dialogState === 'Open'} maxWidth={"md"}>
        <DialogTitle id="simple-dialog-title" disableTypography>Adjust information for <span>{medicationName}</span></DialogTitle>
        <DialogContent>
            <ComponentLoader inProgress={progressState === "Loading"} error={progressState === 'Error'} empty={false}>
                {progressState === 'Loaded' ?
                    <Box>
                        {(medSummary.courseType === CourseType.Regular || medSummary.courseType === CourseType.ShortCourse) &&
                            <PackingStatusEditor selectedPacking={medSummary.packagingType} onChange={type => doseTimeManager.current?.setMedicationPacking(type)} />}
                        <DoseTimesEditorForm
                            instructions={orderInstruction?.textInstruction || ''}
                            doseTimes={orderInstruction?.doseTimes || []}
                            routeOfAdministration={orderInstruction?.routeOfAdministration || null}
                            frequency={orderInstruction?.frequency || null}
                            onChange={(val) => doseTimeManager.current?.changeDoseTimes(val)}
                            startsOn={orderInstruction?.startsOn || ''}
                        />
                    </Box> : <></>
                }
            </ComponentLoader>
        </DialogContent>
        <DialogActions>
            <Button
                color="primary"
                variant={"outlined"}
                disableElevation={true}
                onClick={() => {
                    changeDialogState('Close');
                    onCancel();
                }}>
                Cancel
            </Button>
            <Button
                color="primary"
                variant={"contained"}
                disableElevation={true}
                disabled={progressState === 'Error'}
                onClick={() => {
                    logger.log('New dose times', doseTimes);
                    doseTimeManager.current?.confirmChanges();
                }}
            >
                Confirm
            </Button>
        </DialogActions>
    </Dialog>;
}

